/**
 * Profiles table (PRD §1 "Profiles table", §5.1 #4, #6-#10, §4.2; redesign § Profiles table).
 *
 * Columns: name (avatar + name with the profile URL as a small external-link icon after it, and
 * the server-formatted follower count beneath), notes, last scrape (relative, absolute on hover),
 * next scrape, mode, derived status badge (last error on hover) and group membership. Sortable by
 * every column, default by name — `GET /api/profiles` has no sort parameter, so the ordering of
 * this one small list is done here; nothing else is derived.
 *
 * **Three cells are editors, not links.** Notes (an in-cell input), Mode (a radio menu) and Groups
 * (a checkbox popup) drop the stretched `.cell-linked` overlay and hold their own trigger, so the
 * row's click-to-navigate area is Name / Last scrape / Next scrape / Status. That is deliberate:
 * the alternative — three clicks through the kebab for every mode change — is what this replaces.
 * The kebab keeps both blocks anyway (same components), so nothing is only reachable one way.
 * On an archived row Mode and Groups fall back to inert text: scheduling is stopped and Restore
 * resets the mode to Manual, so offering an editor there would be a lie. Notes stays editable —
 * it is an annotation, not a schedule.
 */

import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  PROFILE_NOTES_MAX_CHARS,
  SCHEDULE_MODES,
  type GroupDTO,
  type ProfileRowDTO,
  type ProfileStatus,
} from '@lw/shared';
import { viewPath } from '../../app/routes';
import { describeError } from '../../api/client';
import {
  Avatar,
  Badge,
  ConfirmDialog,
  EmptyState,
  ExternalLinkIcon,
  KebabIcon,
  Menu,
  MenuItem,
  MenuSeparator,
  Spinner,
  cx,
} from '../../components';
import { formatDateTime, formatRelativeTime } from '../../lib/datetime';
import { SCHEDULE_MODE_LABEL } from '../../lib/labels';
import { useMediaEnabled } from '../settings/useSettings';
import { GroupTags } from './GroupTags';
import { NotificationsMenuItems } from './NotificationsMenuItems';
import { ProfileGroupsMenuItems } from './ProfileGroupsMenuItems';
import { ScheduleModeMenuItems } from './ScheduleModeMenuItems';
import { SortableHeader, type SortState } from './SortableHeader';
import {
  useArchiveProfile,
  useRestoreProfile,
  useSetProfileGroups,
  useSetNotifications,
  useSetProfileNotes,
  useSetScheduleMode,
  useSetWatching,
} from './useHomeData';
import { WatchToggle } from './WatchToggle';

export const PROFILE_SORT_KEYS = [
  'name',
  'notes',
  'lastScrapedAt',
  'nextScrapeAt',
  'scheduleMode',
  'watching',
  'status',
  'groups',
] as const;
export type ProfileSortKey = (typeof PROFILE_SORT_KEYS)[number];

export type ProfileSort = SortState<ProfileSortKey>;

/** Archive dropdown: `active` is the default and reproduces the old "Show active" view. */
export type ArchiveFilter = 'all' | 'active' | 'archived';
/** Group dropdown: `all`, the ungrouped rows, or one group id. */
export type GroupFilter = 'all' | 'none' | number;

/** Display order of the status badge when sorting by status: worst first. */
const STATUS_RANK: Record<ProfileStatus, number> = {
  error: 0,
  mapper_error: 1,
  scraping: 2,
  queued: 3,
  never_scraped: 4,
  idle: 5,
};

function displayName(profile: ProfileRowDTO): string {
  return profile.name ?? profile.slug;
}

const TEXT_SORT_KEYS: ProfileSortKey[] = ['name', 'notes', 'groups'];
/** Text keys whose empty value renders "—" and must sink like a null numeric one. */
const NULLABLE_TEXT_SORT_KEYS: ProfileSortKey[] = ['notes', 'groups'];

function sortProfiles(rows: ProfileRowDTO[], sort: ProfileSort, groupNames: Map<number, string>) {
  const factor = sort.dir === 'asc' ? 1 : -1;
  const textual = TEXT_SORT_KEYS.includes(sort.key);
  const textSinks = NULLABLE_TEXT_SORT_KEYS.includes(sort.key);

  const text = (profile: ProfileRowDTO): string => {
    if (sort.key === 'groups') {
      return profile.groupIds
        .map((id) => groupNames.get(id) ?? '')
        .sort()
        .join(', ');
    }
    if (sort.key === 'notes') return profile.notes ?? '';
    return displayName(profile);
  };

  const numeric = (profile: ProfileRowDTO): number | null => {
    switch (sort.key) {
      case 'lastScrapedAt':
        return profile.lastScrapedAt;
      case 'nextScrapeAt':
        return profile.nextScrapeAt;
      case 'scheduleMode':
        return SCHEDULE_MODES.indexOf(profile.scheduleMode);
      case 'watching':
        // Watched rows sort together; the default direction for a numeric key is descending, so
        // the first click on the header surfaces them.
        return profile.watching.effectiveOn ? 1 : 0;
      case 'status':
        return STATUS_RANK[profile.status];
      default:
        return null;
    }
  };

  return [...rows].sort((a, b) => {
    let result: number;
    if (textual) {
      const left = text(a);
      const right = text(b);
      // Empty text renders "—", so it sinks in both directions like a null numeric cell does —
      // ascending by Notes should surface the annotated rows, not the blank ones.
      if (textSinks && (left === '' || right === '')) {
        if (left !== right) return left === '' ? 1 : -1;
        result = 0;
      } else {
        result = left.localeCompare(right);
      }
    } else {
      const left = numeric(a);
      const right = numeric(b);
      // "—" cells sink to the bottom in both directions.
      if (left === null || right === null) {
        if (left !== right) return left === null ? 1 : -1;
        result = 0;
      } else {
        result = left - right;
      }
    }
    if (result !== 0) return result * factor;
    return displayName(a).localeCompare(displayName(b)) || a.id - b.id;
  });
}

export type ProfilesTableProps = {
  profiles: ProfileRowDTO[];
  groups: GroupDTO[];
  timezone: string;
  archiveFilter: ArchiveFilter;
  groupFilter: GroupFilter;
  /** Ids flagged by `GET /api/status` — highlighted, and the only rows shown while filtering. */
  issueIds: Set<number>;
  issuesFilterOn: boolean;
  onOpenHistory: (profile: ProfileRowDTO) => void;
  onScrapeProfile: (profileId: number) => void;
  onBackfillProfile: (profile: ProfileRowDTO) => void;
  onNewGroup: () => void;
};

export function ProfilesTable({
  profiles,
  groups,
  timezone,
  archiveFilter,
  groupFilter,
  issueIds,
  issuesFilterOn,
  onOpenHistory,
  onScrapeProfile,
  onBackfillProfile,
  onNewGroup,
}: ProfilesTableProps) {
  const navigate = useNavigate();
  const mediaEnabled = useMediaEnabled();
  const [sort, setSort] = useState<ProfileSort>({ key: 'name', dir: 'asc' });
  const [pendingArchive, setPendingArchive] = useState<ProfileRowDTO | null>(null);
  const [editingNotes, setEditingNotes] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  // Escape exits the editor, and the blur it causes must not then commit the reverted draft.
  const cancelledRef = useRef(false);

  const setScheduleMode = useSetScheduleMode();
  const setProfileGroups = useSetProfileGroups();
  const setProfileNotes = useSetProfileNotes();
  const setWatching = useSetWatching();
  const setNotifications = useSetNotifications();
  const archive = useArchiveProfile();
  const restore = useRestoreProfile();

  const groupNames = useMemo(
    () => new Map(groups.map((group) => [group.id, group.name] as const)),
    [groups],
  );

  const rows = useMemo(() => {
    const visible = profiles.filter((profile) => {
      const archived = (profile.archivedAt ?? null) !== null;
      if (archiveFilter === 'active' && archived) return false;
      if (archiveFilter === 'archived' && !archived) return false;
      if (groupFilter === 'none' && profile.groupIds.length > 0) return false;
      if (typeof groupFilter === 'number' && !profile.groupIds.includes(groupFilter)) return false;
      if (issuesFilterOn && !issueIds.has(profile.id)) return false;
      return true;
    });
    return sortProfiles(visible, sort, groupNames);
  }, [profiles, archiveFilter, groupFilter, issuesFilterOn, issueIds, sort, groupNames]);

  const onSort = (key: ProfileSortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: TEXT_SORT_KEYS.includes(key) ? 'asc' : 'desc' },
    );
  };

  const startEditingNotes = (profile: ProfileRowDTO) => {
    cancelledRef.current = false;
    setDraft(profile.notes ?? '');
    setEditingNotes(profile.id);
  };

  const commitNotes = (profile: ProfileRowDTO) => {
    setEditingNotes(null);
    if (cancelledRef.current) return;
    const next = draft.trim();
    // No spurious PATCH when nothing actually changed.
    if (next === (profile.notes ?? '')) return;
    setProfileNotes.mutate({ id: profile.id, notes: next });
  };

  // Which row #70 is writing right now, if any: the switch that was clicked waits, the others stay
  // live (one write must not freeze the column).
  const watchWrite = setWatching.isPending ? setWatching.variables : undefined;

  const saving =
    setScheduleMode.isPending ||
    setProfileGroups.isPending ||
    setProfileNotes.isPending ||
    setWatching.isPending ||
    setNotifications.isPending ||
    restore.isPending;
  // Mode, groups, notes, the Watching toggle and the notification checkboxes have no error surface
  // of their own; the saving bar carries all five.
  const mutationError =
    setProfileNotes.error ??
    setScheduleMode.error ??
    setProfileGroups.error ??
    setWatching.error ??
    setNotifications.error ??
    restore.error;

  if (rows.length === 0) {
    return <EmptyState {...emptyCopy(issuesFilterOn, archiveFilter, groupFilter, groupNames)} />;
  }

  return (
    <>
      <div className="table-wrap">
        {/* `table--profiles` is `table-layout: fixed`: the column widths below are the whole story,
            so no cell's content can resize a column (see home.css). */}
        <table className="table table--fit table--profiles">
          <thead>
            <tr>
              <SortableHeader
                label="Name"
                className="col-name"
                columnKey="name"
                sort={sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Notes"
                className="col-notes"
                columnKey="notes"
                sort={sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Last scrape"
                className="col-when"
                columnKey="lastScrapedAt"
                sort={sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Next scrape"
                className="col-when"
                columnKey="nextScrapeAt"
                sort={sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Mode"
                className="col-mode"
                columnKey="scheduleMode"
                sort={sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Watch"
                className="col-watch"
                columnKey="watching"
                sort={sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Status"
                className="col-status"
                columnKey="status"
                sort={sort}
                onSort={onSort}
              />
              <SortableHeader
                label="Groups"
                className="col-groups"
                columnKey="groups"
                sort={sort}
                onSort={onSort}
              />
              <th className="col-actions" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((profile) => {
              const archived = (profile.archivedAt ?? null) !== null;
              const flagged = issueIds.has(profile.id);
              const to = viewPath('profile', profile.id);
              const goToView = () => void navigate(to);
              const name = displayName(profile);
              // One line, however many groups: `GroupTags` drops the ones that do not fit behind a
              // `+N` chip and carries the full list in its `title`.
              const groupTags =
                profile.groupIds.length === 0 ? (
                  <span className="faint">—</span>
                ) : (
                  <GroupTags
                    names={profile.groupIds.map(
                      (groupId) => groupNames.get(groupId) ?? `#${groupId}`,
                    )}
                  />
                );
              // One labelled tab stop per row (the name): the other cells lead to the same place,
              // so they are links for the pointer and silent to assistive tech.
              const cellLink = (children: ReactNode) => (
                <Link className="row-link" to={to} tabIndex={-1} aria-hidden="true">
                  {children}
                </Link>
              );
              return (
                <tr key={profile.id} className={cx(archived && 'is-archived', flagged && 'is-issue')}>
                  <td>
                    {/* Interleaved links, not one stretched overlay: an `<a>` cannot nest inside
                        another, and the LinkedIn icon and the tags live in this cell. */}
                    <div className="cell-name__wrap">
                      <Link className="row-link" to={to} tabIndex={-1} aria-hidden="true">
                        <Avatar
                          media={profile.avatar}
                          name={name}
                          mediaEnabled={mediaEnabled}
                          size="sm"
                          kind={profile.kind}
                        />
                      </Link>
                      <div className="cell-name__text">
                        <div className="cell-name__line">
                          <Link className="row-link cell-name" to={to} title={name}>
                            {name}
                          </Link>
                          <a
                            className="cell-name__link"
                            href={profile.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label={`Open ${name} on LinkedIn`}
                          >
                            <ExternalLinkIcon size={13} />
                          </a>
                          {archived ? (
                            <span
                              className="tag"
                              title={formatDateTime(profile.archivedAt ?? null, timezone)}
                            >
                              Archived
                            </span>
                          ) : null}
                        </div>
                        {/* Server-formatted count; "followers" is label text, never a computed one. */}
                        {profile.followers.value !== null ? (
                          <Link className="row-link" to={to} tabIndex={-1}>
                            <span className="cell-name__followers">
                              {profile.followers.display} followers
                            </span>
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="cell-notes">
                    {editingNotes === profile.id ? (
                      <input
                        className="input input--cell"
                        autoFocus
                        maxLength={PROFILE_NOTES_MAX_CHARS}
                        value={draft}
                        aria-label={`Note for ${name}`}
                        onChange={(event) => setDraft(event.target.value)}
                        onBlur={() => commitNotes(profile)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.currentTarget.blur();
                          } else if (event.key === 'Escape') {
                            cancelledRef.current = true;
                            event.currentTarget.blur();
                          }
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="cell-edit cell-edit--notes"
                        title={profile.notes ?? undefined}
                        aria-label={`Edit note for ${name}`}
                        onClick={() => startEditingNotes(profile)}
                      >
                        {profile.notes ?? <span className="faint">—</span>}
                      </button>
                    )}
                  </td>
                  <td
                    className="cell-muted cell-linked"
                    title={formatDateTime(profile.lastScrapedAt, timezone)}
                  >
                    {cellLink(
                      profile.lastScrapedAt === null
                        ? '—'
                        : formatRelativeTime(profile.lastScrapedAt),
                    )}
                  </td>
                  <td
                    className="cell-muted cell-linked"
                    title={formatDateTime(profile.nextScrapeAt, timezone)}
                  >
                    {cellLink(
                      profile.nextScrapeAt === null
                        ? '—'
                        : formatRelativeTime(profile.nextScrapeAt),
                    )}
                  </td>
                  {archived ? (
                    <td className="cell-mode cell-muted cell-linked">
                      {cellLink(SCHEDULE_MODE_LABEL[profile.scheduleMode])}
                    </td>
                  ) : (
                    <td className="cell-mode cell-muted">
                      <Menu
                        label={SCHEDULE_MODE_LABEL[profile.scheduleMode]}
                        // Opens with the visible text, so the accessible name still contains it.
                        ariaLabel={`${SCHEDULE_MODE_LABEL[profile.scheduleMode]} — scraping mode for ${name}`}
                        triggerClassName="cell-edit"
                      >
                        {(close) => (
                          <ScheduleModeMenuItems
                            current={profile.scheduleMode}
                            disabled={setScheduleMode.isPending}
                            showLabel={false}
                            onSelect={(scheduleMode) => {
                              close();
                              setScheduleMode.mutate({ id: profile.id, scheduleMode });
                            }}
                          />
                        )}
                      </Menu>
                    </td>
                  )}
                  {/* An editor cell like Mode: no `.cell-linked` overlay, its own control, and the
                      server's finished sentence as the hover title (§12.5). */}
                  <td className="cell-watch">
                    <WatchToggle
                      watching={profile.watching}
                      subjectLabel={name}
                      disabled={archived}
                      pending={
                        watchWrite !== undefined &&
                        watchWrite.t === 'profile' &&
                        watchWrite.id === profile.id
                      }
                      onToggle={(enabled) =>
                        setWatching.mutate({
                          t: 'profile',
                          id: profile.id,
                          body: { enabled },
                        })
                      }
                    />
                  </td>
                  <td className="cell-linked">
                    {cellLink(
                      <Badge status={profile.status} title={profile.lastError ?? undefined} />,
                    )}
                  </td>
                  {archived ? (
                    <td className="cell-groups cell-linked">{cellLink(groupTags)}</td>
                  ) : (
                    <td className="cell-groups">
                      <Menu
                        label={groupTags}
                        ariaLabel={`Groups for ${name}`}
                        triggerClassName="cell-edit"
                        panelClassName="menu__panel--wide"
                      >
                        {(close) => (
                          <ProfileGroupsMenuItems
                            groups={groups}
                            selected={profile.groupIds}
                            showLabel={false}
                            onChange={(groupIds) =>
                              setProfileGroups.mutate({ id: profile.id, groupIds })
                            }
                            onNewGroup={() => {
                              close();
                              onNewGroup();
                            }}
                          />
                        )}
                      </Menu>
                    </td>
                  )}
                  <td className="cell-menu">
                    <Menu
                      label={<KebabIcon />}
                      ariaLabel={`Actions for ${displayName(profile)}`}
                      align="right"
                      triggerClassName="icon-btn row-menu"
                      panelClassName="menu__panel--wide"
                    >
                      {(close) =>
                        archived ? (
                          <>
                            <MenuItem
                              onClick={() => {
                                close();
                                goToView();
                              }}
                            >
                              View
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                close();
                                onOpenHistory(profile);
                              }}
                            >
                              Scrape history
                            </MenuItem>
                            <MenuSeparator />
                            <MenuItem
                              onClick={() => {
                                close();
                                restore.mutate(profile.id);
                              }}
                            >
                              Restore (returns as Manual)
                            </MenuItem>
                          </>
                        ) : (
                          <>
                            <MenuItem
                              onClick={() => {
                                close();
                                goToView();
                              }}
                            >
                              View
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                close();
                                onScrapeProfile(profile.id);
                              }}
                            >
                              Scrape now
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                close();
                                onBackfillProfile(profile);
                              }}
                            >
                              Backfill…
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                close();
                                onOpenHistory(profile);
                              }}
                            >
                              Scrape history
                            </MenuItem>
                            <MenuSeparator />
                            <ScheduleModeMenuItems
                              current={profile.scheduleMode}
                              disabled={setScheduleMode.isPending}
                              onSelect={(scheduleMode) => {
                                close();
                                setScheduleMode.mutate({ id: profile.id, scheduleMode });
                              }}
                            />
                            <MenuSeparator />
                            <ProfileGroupsMenuItems
                              groups={groups}
                              selected={profile.groupIds}
                                onChange={(groupIds) =>
                                setProfileGroups.mutate({ id: profile.id, groupIds })
                              }
                              onNewGroup={() => {
                                close();
                                onNewGroup();
                              }}
                            />
                            <MenuSeparator />
                            {/* Checkboxes, so the menu stays open across both toggles. */}
                            <NotificationsMenuItems
                              current={profile.notifications}
                              disabled={setNotifications.isPending}
                              onToggle={(patch) =>
                                setNotifications.mutate({
                                  t: 'profile',
                                  id: profile.id,
                                  body: patch,
                                })
                              }
                            />
                            <MenuSeparator />
                            <MenuItem
                              destructive
                              onClick={() => {
                                close();
                                setPendingArchive(profile);
                              }}
                            >
                              Archive
                            </MenuItem>
                          </>
                        )
                      }
                    </Menu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {saving ? (
        <div className="table-saving" role="status">
          <Spinner size="sm" />
          Saving…
        </div>
      ) : null}

      {!saving && mutationError !== null ? (
        <div className="table-saving table-saving--error" role="alert">
          {describeError(mutationError)}
        </div>
      ) : null}

      {pendingArchive !== null ? (
        <ConfirmDialog
          open
          destructive
          title={`Archive “${displayName(pendingArchive)}”?`}
          confirmLabel="Archive profile"
          busy={archive.isPending}
          message={
            <p>
              Archiving stops all scraping for this profile — its own schedule and any group
              schedules — and removes it from group views. Nothing is deleted: every post, snapshot
              and raw payload is kept, and you can restore it at any time.
            </p>
          }
          onCancel={() => setPendingArchive(null)}
          onConfirm={() =>
            archive.mutate(pendingArchive.id, { onSuccess: () => setPendingArchive(null) })
          }
        />
      ) : null}
    </>
  );
}

/** Which of the (composable) filters emptied the table decides what the empty state says. */
function emptyCopy(
  issuesFilterOn: boolean,
  archiveFilter: ArchiveFilter,
  groupFilter: GroupFilter,
  groupNames: Map<number, string>,
): { title: string; description: string } {
  if (issuesFilterOn) {
    return {
      title: 'No profiles with issues',
      description: 'Every tracked profile is healthy right now.',
    };
  }
  if (typeof groupFilter === 'number') {
    return {
      title: 'No profiles in this group',
      description: `Nothing matches “${groupNames.get(groupFilter) ?? 'this group'}” — pick another group, or add members from a row's Groups cell.`,
    };
  }
  if (groupFilter === 'none') {
    return {
      title: 'Every profile is in a group',
      description: 'No ungrouped profiles to show.',
    };
  }
  if (archiveFilter === 'archived') {
    return { title: 'No archived profiles', description: 'Nothing has been archived yet.' };
  }
  if (archiveFilter === 'active') {
    return {
      title: 'No active profiles',
      description: 'Every profile is archived — switch Archive to All or Archived to see them.',
    };
  }
  return { title: 'No profiles to show', description: 'Nothing here yet.' };
}

export default ProfilesTable;
